import { normalizeWidgetTheme, APIURL, cancelSelectEvent, getCSSStyles, getTraderSMCardHTML, getWidgetDataURI, apiGet, toggleWidgetTheme, getEChartsTooltipTheme, isHeaderPinned, getHeaderPinStateClass, getHeaderPinButtonHTML, setupHeaderPin, getNoDataHTML } from './../../helpers';
import { WidgetConfig, WidgetEventHandler, WidgetEventType, WidgetEvent } from './../../types';

interface MarketPulseSeries {
  [symbol: string]: [timestamp: number, value: number][];
}

interface MarketPulseConfig extends WidgetConfig {
  index?: string[];
  interval?: string;
  onIndexChange?: (index: string[]) => void;
  onIntervalChange?: (interval: string) => void;
}

export class TraderverseMarketPulse {
  private container: HTMLElement;
  private config: MarketPulseConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private shadowRoot: ShadowRoot;
  private chartInstance: any = null;
  private marketPulseData: MarketPulseSeries = {};
  private trendingIndexes: Array<{ value: string; label: string }> = [];
  private eventListenersBound = false;

  constructor(config: MarketPulseConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }

    this.init();
  }

  private getDefaultConfig(): Partial<MarketPulseConfig> {
    return {
      headerPinned: true,
      theme: 'white',
      width: '100%',
      height: '400px',
      autosize: true
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const element = document.getElementById(container) || document.querySelector(container);
      if (!element) {
        throw new Error(`Container not found: ${container}`);
      }
      return element as HTMLElement;
    }
    return container;
  }

  private async loadECharts(): Promise<void> {
    if (!(window as any).echarts) {
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/echarts@5/dist/echarts.min.js';
      document.head.appendChild(script);

      return new Promise((resolve) => {
        script.onload = () => resolve();
      });
    }
  }


  private async init(): Promise<void> {
    this.attachStyles();

    await this.createWidget();

    await this.loadECharts();
    // Fetch trending indexes for dropdown
    this.trendingIndexes = await this.fetchTrendingIndexes();
    if (!this.config.index && this.trendingIndexes.length > 0) {
      this.config.index = [this.trendingIndexes[0].value];
    }
    if (!this.config.interval) {
      this.config.interval = '1m';
    }

    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    this.setupEventListeners();
    await this.loadMarketPulseData();
    if (widget) widget.classList.remove('loading');
    cancelSelectEvent(this.shadowRoot);
    this.emit('ready', {});
    this.config.onReady?.();
  }

  // Fetch trending indexes for dropdown (from trending_indexes API)
  private async fetchTrendingIndexes(): Promise<{ value: string, label: string }[]> {
    try {
      const res = await apiGet({
        method: 'POST',
        url: `${APIURL}/v1/discover/trending_indexes`,
        body: {},
        headers: {
          'Content-Type': 'application/json'
        }
      });
      if (!res.ok) throw new Error('API Error');
      const data = await res.json();
      const records = data?.data?.records || [];
      return records.map((item: any) => ({ value: item.ticker[0], label: item.ticker[0] }));
    } catch (err) {
      console.error('Error fetching trending indexes:', err);
      return [];
    }
  }

  private async createWidget(): Promise<void> {
    let themeClass = 'defaultDarkMode';
  let dataTheme = 'default-dark';
  
  if (this.config.theme === 'white') {
    themeClass = 'whiteMode';
    dataTheme = 'white';
  } else if (this.config.theme === 'black') {
    themeClass = 'blackMode';
    dataTheme = 'black';
  } else if (this.config.theme === 'default-lite') {
  themeClass = 'defaultLightMode';
  dataTheme = 'default-lite';
}
    const indexOptions = this.trendingIndexes.map(idx => `<option value="${idx.value}">${idx.label}</option>`).join('');
    const indexChkOptions = this.trendingIndexes.map(idx => {
      const isChecked = this.config.index?.includes(idx.value) ? 'checked' : '';
      return `<div class="checkBoxField"> <input class="filter-checkbox" value="${idx.value}" type="checkbox" ${isChecked} /> <label>${idx.label} <i>✓</i></label> </div>`;
    }).join('');
    const widgetHtml = `
   <div style="width: ${typeof this.config.width == "number" ? this.config.width+ 'px' : this.config.width  };height: ${typeof this.config.height == "number" ? this.config.height+ 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
          <div class="headTop">
            <h4 class="title market">${this.getTitleIconSvg()} Market Pulse</h4>
            <div class="actionCol">${getHeaderPinButtonHTML(this.config)}<div class="item">
                <button class="filters-toggle" type="button"> <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg> </button>
              </div></div>
          </div>
          <div class="inBody">

            <div class="optionsRow">
              <div class="item">
                <select  class="period-dropdown">
                  <option value="1d">1 Day</option>
                  <option value="1w">1 Week</option>
                  <option value="1m" selected>1 Month</option>
                  <option value="3m">3 Months</option>
                  <option value="6m">6 Months</option>
                  <option value="1y">1 Year</option>
                </select>
              </div>
              <span></span>

              <div class="filtersBox sideBar hidden">
                  <div class="item checkBoxes">
                      ${indexChkOptions}
                  </div>
              </div>

            </div>

            <div class="chartDv">
              <div id="chart" class="chart"></div>
            </div>

          </div>
        </div>

    
    `;

    this.shadowRoot.innerHTML = widgetHtml;
    this.initializeChart();
  }

  private async initializeChart(): Promise<void> {
    console.log('Initializing chart...');
    await this.loadECharts();

    // Wait longer for the DOM to be ready and properly laid out
    setTimeout(() => {
      this.renderChart();
      this.setupEventListeners();
      this.setupResizeObserver();
    }, 300);
  }

  private async attachStyles(): Promise<void> {
    try {
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private formatLastUpdated(date: Date): string {
    const options: Intl.DateTimeFormatOptions = {
      month: '2-digit',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'America/New_York',
    };
    return date.toLocaleString('en-US', options).replace(',', '').replace(/\//g, '-') + ' EST';
  }



  private renderChart(): void {
    const chartElement = this.shadowRoot.getElementById('chart');
    const chartDv = this.shadowRoot.querySelector('.chartDv') as HTMLElement;
    if (!chartElement) {
      console.error('Chart element not found');
      return;
    }

    const echarts = (window as any).echarts;
    if (!echarts) {
      console.error('ECharts library not loaded');
      return;
    }

    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }

    if (!this.marketPulseData || Object.keys(this.marketPulseData).length === 0) {
      chartDv?.removeAttribute('data-testing-id');
      chartElement.innerHTML = getNoDataHTML();
      return;
    }

    chartElement.innerHTML = '';
    this.chartInstance = echarts.init(chartElement);

    // Collect all unique dates (timestamps) from all series
    const allDatesSet = new Set<number>();
    Object.values(this.marketPulseData).forEach(series => {
      series.forEach(([timestamp]) => allDatesSet.add(timestamp));
    });
    const allDates = Array.from(allDatesSet).sort((a, b) => a - b);
    const dateLabels = allDates.map(ts => {
      const date = new Date(ts);
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    });

    // Build series for each symbol
    const colorPalette = ['#00FF88', '#FF0088', '#0088FF', '#FF8800', '#8800FF', '#00FFDD', '#FFDD00'];
    const series = Object.entries(this.marketPulseData).map(([symbol, data], idx) => {
      // Map values to allDates, fill missing with null
      const dateToValue = new Map<number, number>();
      data.forEach(([timestamp, value]) => {
        dateToValue.set(timestamp, typeof value === 'number' ? value : parseFloat(value));
      });
      const values = allDates.map(ts => {
        const v = dateToValue.get(ts);
        return typeof v === 'number' && !isNaN(v) ? v : null;
      });
      return {
        name: symbol,
        type: 'line',
        data: values,
        smooth: true,
        symbol: 'none',
        lineStyle: {
          color: colorPalette[idx % colorPalette.length],
          width: 3
        },
        areaStyle: idx === 0 ? {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(0, 255, 136, 0.4)' },
              { offset: 1, color: 'rgba(0, 255, 136, 0.1)' }
            ]
          }
        } : undefined
      };
    });

    // const isBlack = this.config.theme === 'black';
    const widgetBox = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    const textColor = getComputedStyle(widgetBox).getPropertyValue('--charts-text-color');
    const axisLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-axis-color');
    const splitLineColor = getComputedStyle(widgetBox).getPropertyValue('--charts-split-line-color');
    const fontSize = getComputedStyle(widgetBox).getPropertyValue('--font-size-sm');

    const option = {
      backgroundColor: 'transparent',
      grid: { left: 0, right: 0, top: 10, bottom: 0, containLabel: true },
      xAxis: {
        type: 'category',
        data: dateLabels,
        axisLine: {
          lineStyle: {
            color: axisLineColor
          }
        },
        axisLabel: { 
          color: textColor, fontSize: fontSize,
          interval: Math.max(0, Math.floor(dateLabels.length / 6))
        },
        axisTick: {
          show: false
        }
      },
      yAxis: {
        type: 'value',
        axisLine: {
          show: false
        },
        axisLabel: {
          color: textColor,
          fontSize: fontSize,
          formatter: (value: any) => `${value.toFixed(2)}%`
        },
        axisTick: {
          show: false
        },
        splitLine: {
          lineStyle: {
            color: splitLineColor,
            type: 'dashed'
          }
        }
      },
      series,
      tooltip: {
        trigger: 'axis',
        ...getEChartsTooltipTheme(widgetBox),
        formatter: (params: any) => {
          let tooltip = `${params[0].name}<br/>`;
          params.forEach((data: any) => {
            tooltip += `${data.marker} <b>${data.seriesName}</b>: ${data.value !== null ? data.value + '%' : 'N/A'}<br/>`;
          });
          return tooltip;
        }
      }
    };
    this.chartInstance.setOption(option);
    setTimeout(() => {
      if (this.chartInstance) {
        this.chartInstance.resize();
      }
    }, 100);
  }

  private generateMockData(): void {
    console.log('Generating mock data for chart');
    const mockData: MarketPulseSeries = {};
    const symbol = this.config.index?.[0] || 'SPY';
    const now = Date.now();
    const dataPoints: [number, number][] = [];

    // Generate 30 days of mock data
    for (let i = 29; i >= 0; i--) {
      const timestamp = now - (i * 24 * 60 * 60 * 1000);
      const value = -0.5 + (29 - i) * 0.05 + (Math.random() - 0.5) * 0.2;
      dataPoints.push([timestamp, value]);
    }

    mockData[symbol] = dataPoints;
    this.marketPulseData = mockData;
    console.log('Generated mock data:', mockData);
  }


  private changeInterval(interval: string): void {
    this.config.interval = interval;
    this.emit('intervalChange', { interval });
    this.config.onIntervalChange?.(interval);
    this.loadMarketPulseData(this.config.index, interval);
  }

  private changeIndex(index: string[]): void {
    this.config.index = index;
    this.emit('indexChange', { index });
    this.config.onIndexChange?.(index);
    this.loadMarketPulseData(index, this.config.interval);
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
    if (this.eventListenersBound) {
      return;
    }
    this.eventListenersBound = true;

    const indexDropdown = this.shadowRoot.querySelector('.index-dropdown') as HTMLSelectElement;
    const periodDropdown = this.shadowRoot.querySelector('.period-dropdown') as HTMLSelectElement;
    const filterButton = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement;
    const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement;

    if (indexDropdown) {
      indexDropdown.addEventListener('change', (e) => {
        const target = e.target as HTMLSelectElement;
        const selected = Array.from(target.selectedOptions).map(opt => opt.value);
        const prev = this.config.index || [];
        const merged = Array.from(new Set([...prev, ...selected]));
        this.changeIndex(merged);
      });
      if (this.config.index && this.config.index.length > 0) {
        Array.from(indexDropdown.options).forEach(opt => {
          if (this.config.index!.includes(opt.value)) {
            opt.selected = true;
          }
        });
      }
    }

    if (periodDropdown) {
      periodDropdown.addEventListener('change', (e) => {
        const target = e.target as HTMLSelectElement;
        this.changeInterval(target.value);
      });
      // Set initial value
      if (this.config.interval) {
        periodDropdown.value = this.config.interval;
      }
    }

    if (filterButton && filtersBox) {
      filterButton.addEventListener('click', () => {
        const isHidden = filtersBox.classList.toggle('hidden');
        filterButton.setAttribute('aria-expanded', `${!isHidden}`);
      });
    }

    // Handle checkbox changes - trigger API call immediately
    const checkboxes = this.shadowRoot.querySelectorAll('.filter-checkbox');
    checkboxes.forEach(checkbox => {
      checkbox.addEventListener('change', async (e) => {
        const selectedIndices: string[] = [];
        const allCheckboxes = this.shadowRoot.querySelectorAll('.filter-checkbox');
        allCheckboxes.forEach((cb) => {
          const input = cb as HTMLInputElement;
          if (input.checked) {
            selectedIndices.push(input.value);
          }
        });
        
        console.log('Checkbox changed - triggering API with selected indices:', selectedIndices);
        
        // Trigger the API with the selected indices
        await this.applySelectedFilters(selectedIndices);
        
        // Update the config and reload data
        this.changeIndex(selectedIndices);
      });
    });
  }

  private async applySelectedFilters(index: string[]): Promise<void> {
    try {
      console.log('Triggering API with payload:', { index });
      
      const response = await apiGet({
        method: 'POST',
        url: `${APIURL}/v1/discover/chart`,
        body: { index, interval: this.config.interval || '1m' },
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const responseData = await response.json();
      console.log('API response:', responseData);
      
      return responseData;
    } catch (error) {
      console.error('Error applying filters:', error);
      throw error;
    }
  }

  private async loadMarketPulseData(index: string[] = this.config.index || ["SPY"], interval: string = this.config.interval || "1m"): Promise<void> {
    const widget = this.shadowRoot.querySelector('.chartDv') as HTMLElement;
    if (widget) widget.classList.add('loading');
    try {
      console.log('Loading market pulse data for:', { index, interval });
      const data = await this.fetchMarketPulseData(index, interval);
      console.log('Received data:', data);

      const hasData = data && Object.values(data).some(
        (series) => Array.isArray(series) && series.length > 0
      );
      if (hasData) {
        widget?.setAttribute('data-testing-id', 'market-pulse-chart');
        this.marketPulseData = data;
      } else {
        widget?.removeAttribute('data-testing-id');
        this.marketPulseData = {};
      }

      this.renderChart();
    } catch (error) {
      console.error('Error loading market pulse data:', error);
      widget?.removeAttribute('data-testing-id');
      this.marketPulseData = {};
      this.renderChart();
    } finally {
      if (widget) widget.classList.remove('loading');
    }
  }

  private async fetchMarketPulseData(index: string[], interval: string): Promise<MarketPulseSeries> {
    return new Promise((resolve) => {
      apiGet({
        method: 'POST',
        url: `${APIURL}/v1/discover/chart`,
        body: { index, interval },
        headers: {
          'Content-Type': 'application/json'
        }
      }).then((response) => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      }).then((responseData) => {
        console.log('Fetched market pulse data:', responseData.data);
        resolve(responseData.data);
      }).catch((error) => {
        console.error('Error fetching market pulse data:', error);
        resolve({});
      });
    });
  }

  private getPeriodDays(period: string): number {
    switch (period) {
      case '1d': return 1;
      case '1w': return 7;
      case '1m': return 30;
      case '3m': return 90;
      case '6m': return 180;
      case '1y': return 365;
      default: return 30;
    }
  }


  private toggleTheme(): void {
    const widget = this.shadowRoot.querySelector('.traderverse-widget');
    if (widget) {
      const currentTheme = widget.getAttribute('data-theme');
      const newTheme = currentTheme === 'black' ? 'white' : 'black';
      widget.setAttribute('data-theme', newTheme);
      this.config.theme = newTheme as 'white' | 'black';
    }
  }

  private toggleFullscreen(): void {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      this.container.requestFullscreen?.();
    }
  }

  // Public API methods
  public on(eventType: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(eventType)) {
      this.eventHandlers.set(eventType, []);
    }
    this.eventHandlers.get(eventType)!.push(handler);
  }

  public off(eventType: WidgetEventType, handler: WidgetEventHandler): void {
    const handlers = this.eventHandlers.get(eventType);
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index > -1) {
        handlers.splice(index, 1);
      }
    }
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.traderverse-widget') as HTMLElement;
    return await getWidgetDataURI(widget);
  }



  
  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;
  
    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';
  
    // this.updateDisplay();
  }

  public refresh(): void {
    this.loadMarketPulseData();
  }

  public resize(): void {
    if (this.chartInstance) {
      const chartElement = this.shadowRoot.getElementById('chart');
      if (chartElement) {
        const rect = chartElement.getBoundingClientRect();
        console.log('Resizing chart with current dimensions:', { width: rect.width, height: rect.height });

        if (rect.height === 0) {
          chartElement.style.height = '350px';
        }
      }
      this.chartInstance.resize();
    }
  }

  private setupResizeObserver(): void {
    const chartElement = this.shadowRoot.getElementById('chart');
    if (chartElement && 'ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver((entries) => {
        for (const entry of entries) {
          console.log('Chart element resized:', entry.contentRect);
          if (this.chartInstance) {
            setTimeout(() => this.chartInstance.resize(), 10);
          }
        }
      });
      resizeObserver.observe(chartElement);
    }
  }

  public destroy(): void {
    if (this.chartInstance) {
      this.chartInstance.dispose();
    }
    this.eventHandlers.clear();
    this.shadowRoot.innerHTML = '';
  }

  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) {
      const event: WidgetEvent = { type, data };
      handlers.forEach(handler => handler(event));
    }
  }
  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> <path d="M9.16252 19.05C8.96252 19.05 8.76252 18.9875 8.58752 18.85C8.17502 18.5375 8.10002 17.95 8.41252 17.5375L11.3875 13.675C11.75 13.2125 12.2625 12.9125 12.85 12.8375C13.425 12.7625 14.0125 12.925 14.475 13.2875L16.7625 15.0875C16.85 15.1625 16.9375 15.1625 17 15.15C17.05 15.15 17.1375 15.125 17.2125 15.025L20.1 11.3C20.4125 10.8875 21.0125 10.8125 21.4125 11.1375C21.825 11.45 21.9 12.0375 21.575 12.45L18.6875 16.175C18.325 16.6375 17.8125 16.9375 17.225 17C16.6375 17.075 16.0625 16.9125 15.6 16.55L13.3125 14.75C13.225 14.675 13.125 14.675 13.075 14.6875C13.025 14.6875 12.9375 14.7125 12.8625 14.8125L9.88752 18.675C9.72502 18.925 9.45002 19.05 9.16252 19.05Z" fill="currentColor"/> </svg>`;
  }

}

// Global registration for script tag usage
declare global {
  interface Window {
    TraderverseMarketPulse: typeof TraderverseMarketPulse;
  }
}

if (typeof window !== 'undefined') {
  window.TraderverseMarketPulse = TraderverseMarketPulse;
}
